import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import InfoPopup from './InfoPopup';

const CardContainer = styled(motion.div)`
  background-color: var(--card-background);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  width: 100%;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 8px;
    background: linear-gradient(to right, var(--primary-color), var(--accent-color));
    z-index: 1;
  }
`;

const GodImage = styled.div`
  width: 100%;
  height: 360px;
  background-image: ${props => `url(${props.$image})`};
  background-size: cover;
  background-position: center top;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 70%;
    background: linear-gradient(to top, rgba(26, 13, 58, 0.95), transparent);
  }
`;

const GodInfo = styled.div`
  padding: 1.5rem;
  position: relative;
  z-index: 2;
  margin-top: -120px;
`;

const GodName = styled.h2`
  font-size: 2.2rem;
  color: var(--text-color);
  margin-bottom: 0.25rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
`;

const GodTitle = styled.h3`
  font-size: 1.2rem;
  color: var(--accent-color);
  margin-bottom: 1rem;
  font-weight: 400;
  font-style: italic;
`;

const PantheonBadge = styled.span`
  background-color: var(--primary-color);
  color: var(--text-color);
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
  display: inline-block;
  margin-bottom: 1rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const StatsContainer = styled.div`
  margin-top: 1.5rem;
`;

const StatsTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  
  &::after {
    content: '';
    flex-grow: 1;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
    margin-left: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const StatItem = styled.div`
  background-color: rgba(46, 23, 96, 0.4);
  padding: 0.8rem 0.5rem;
  border-radius: var(--border-radius);
  text-align: center;
  transition: transform 0.2s ease, background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(92, 67, 169, 0.4);
    transform: translateY(-2px);
  }
`;

const StatName = styled.p`
  font-size: 0.8rem;
  color: var(--accent-color);
  margin-bottom: 0.25rem;
`;

const StatValue = styled.p`
  font-size: 1.1rem;
  font-weight: 700;
`;

const AbilitiesTabs = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AbilitiesTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  
  &::after {
    content: '';
    flex-grow: 1;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
    margin-left: 1rem;
  }
`;

const TabButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  background-color: ${props => props.$active ? 'var(--accent-color)' : 'rgba(46, 23, 96, 0.4)'};
  color: ${props => props.$active ? 'var(--background-color)' : 'var(--text-color)'};
  border-radius: 4px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$active ? 'var(--accent-color)' : 'rgba(92, 67, 169, 0.4)'};
    transform: translateY(-2px);
  }
`;

const AbilityContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
`;

const AbilityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  &::after {
    content: 'Click for details';
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    font-size: 0.7rem;
    color: var(--text-color);
    opacity: 0;
    background-color: rgba(0, 0, 0, 0.5);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    transition: opacity 0.2s ease;
  }
  
  &:hover::after {
    opacity: 0.7;
  }
`;

const AbilityNameSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AbilityIcon = styled.div`
  width: 32px;
  height: 32px;
  background-image: ${props => props.$image ? `url(${props.$image})` : 'none'};
  background-size: cover;
  background-position: center;
  border-radius: 4px;
  background-color: ${props => props.$image ? 'transparent' : 'rgba(46, 23, 96, 0.6)'};
  flex-shrink: 0;
`;

const AbilityName = styled.h5`
  font-size: 1rem;
  color: var(--accent-color);
  margin: 0;
`;

const AbilityType = styled.span`
  font-size: 0.7rem;
  background-color: rgba(92, 67, 169, 0.6);
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
`;

const StanceTabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin: 0.5rem 0;
`;

const StanceTab = styled.button`
  background-color: ${props => props.$active ? 'var(--accent-color)' : 'rgba(46, 23, 96, 0.6)'};
  color: ${props => props.$active ? 'var(--background-color)' : 'var(--text-color)'};
  border-radius: 4px;
  padding: 0.3rem 0.6rem;
  font-size: 0.7rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$active ? 'var(--accent-color)' : 'rgba(92, 67, 169, 0.4)'};
  }
`;

const AbilityDescription = styled.p`
  font-size: 0.9rem;
  margin: 0.5rem 0;
  white-space: pre-wrap;
`;

const InteractionsList = styled.ul`
  margin: 0.5rem 0;
  padding-left: 1.2rem;
  font-size: 0.8rem;
  color: var(--accent-color-light, #a899e0);
`;

const InteractionItem = styled.li`
  margin-bottom: 0.25rem;
`;

const AspectBox = styled.div`
  background-color: rgba(92, 67, 169, 0.2);
  border-left: 3px solid var(--accent-color);
  padding: 0.5rem;
  margin: 0.5rem 0;
  font-size: 0.85rem;
`;

const GodCard = ({ god }) => {
  const [activeTab, setActiveTab] = useState('stats');
  const [activeStances, setActiveStances] = useState({});
  const [selectedAbility, setSelectedAbility] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  
  const imageUrl = god.card_url || god.image_url || 'https://via.placeholder.com/400x600?text=God+Image';
  
  const mainStats = [
    { name: 'Health', value: god.base_stats['Max Health'] || 'N/A' },
    { name: 'Mana', value: god.base_stats['Max Mana'] || 'N/A' },
    { name: 'Phys. Protection', value: god.base_stats['Physical Protection'] || 'N/A' },
    { name: 'Mag. Protection', value: god.base_stats['Magical Protection'] || 'N/A' },
    { name: 'Movement Speed', value: god.base_stats['Movement Speed'] || 'N/A' },
    { name: 'Attack Speed', value: god.base_stats['Base Attack Speed'] || 'N/A' }
  ];
  
  const handleStanceChange = (abilityIndex, stance) => {
    setActiveStances(prev => ({
      ...prev,
      [abilityIndex]: stance
    }));
  };
  
  const handleAbilityClick = (ability) => {
    setSelectedAbility(ability);
    setShowPopup(true);
  };
  
  // Detect if an ability description contains multiple stance descriptions
  const detectStanceDescription = (ability) => {
    // Already tagged as stance switcher
    if (ability.is_stance_switcher && ability.stance_versions && ability.stance_versions.length > 0) {
      return true;
    }
    
    // Look for patterns like "Bow: ... Axe: ..." in the description
    const description = ability.description || '';
    const stanceKeywords = ['Bow:', 'Axe:', 'Bow Stance:', 'Axe Stance:'];
    
    // Count how many stance indicators we have
    let stanceCount = 0;
    for (const keyword of stanceKeywords) {
      if (description.includes(keyword)) {
        stanceCount++;
      }
    }
    
    // If multiple stance indicators, this is a stance description
    return stanceCount > 1;
  };
  
  // Parse an ability description to extract stance versions
  const parseStanceVersions = (ability) => {
    if (ability.stance_versions && ability.stance_versions.length > 0) {
      return ability.stance_versions;
    }
    
    const description = ability.description || '';
    const stances = [];
    
    // Special case for abilities like Expose Weakness where stances are in one line
    // Example: "Bow: Gain bonus Attack Speed. Axe Gain bonus Movement Speed."
    if (description.includes('Bow:') && description.includes('Axe') && 
        !description.includes('Axe:') && description.includes('.')) {
      
      // Try to split by period and look for Bow/Axe segments
      const segments = description.split('.');
      
      let bowDescription = '';
      let axeDescription = '';
      
      for (const segment of segments) {
        const trimmed = segment.trim();
        if (trimmed.startsWith('Bow:')) {
          bowDescription = trimmed.substring(4).trim();
        } else if (trimmed.startsWith('Bow')) {
          bowDescription = trimmed.substring(3).trim();
        } else if (trimmed.startsWith('Axe:')) {
          axeDescription = trimmed.substring(4).trim();
        } else if (trimmed.startsWith('Axe')) {
          axeDescription = trimmed.substring(3).trim();
        }
      }
      
      if (bowDescription) {
        stances.push({
          stance: 'Bow',
          description: bowDescription
        });
      }
      
      if (axeDescription) {
        stances.push({
          stance: 'Axe',
          description: axeDescription
        });
      }
      
      if (stances.length > 0) {
        return stances;
      }
    }
    
    // Enhanced handling for all stance switching abilities
    // Identify all stance labels in the description
    const stanceLabels = [];
    const allStanceKeywords = ['Bow:', 'Axe:', 'Bow Stance:', 'Axe Stance:'];
    
    // Find all stance keywords in the description
    allStanceKeywords.forEach(keyword => {
      let pos = description.indexOf(keyword);
      while (pos !== -1) {
        stanceLabels.push({
          label: keyword.replace(':', ''),
          position: pos,
          length: keyword.length
        });
        pos = description.indexOf(keyword, pos + 1);
      }
    });
    
    // Sort by position to process them in order
    stanceLabels.sort((a, b) => a.position - b.position);
    
    // If we found any stance labels, extract their descriptions
    if (stanceLabels.length > 0) {
      // Group stance labels by their stance name (e.g., "Bow", "Axe")
      const stanceGroups = {};
      
      stanceLabels.forEach((label, index) => {
        const stanceName = label.label;
        if (!stanceGroups[stanceName]) {
          stanceGroups[stanceName] = [];
        }
        
        // Find where this description ends (at the next stance label or end of string)
        const startPos = label.position + label.length + 1; // +1 for the colon and space
        const endPos = index < stanceLabels.length - 1 
          ? stanceLabels[index + 1].position 
          : description.length;
          
        const stanceText = description.substring(startPos, endPos).trim();
        
        // Add this description to the group for this stance
        stanceGroups[stanceName].push(stanceText);
      });
      
      // For each stance, keep only the longest description
      Object.keys(stanceGroups).forEach(stanceName => {
        const descriptions = stanceGroups[stanceName];
        
        if (descriptions.length > 0) {
          // If there are multiple descriptions for this stance, use the longest one
          const longestDescription = descriptions.reduce((longest, current) => 
            current.length > longest.length ? current : longest, descriptions[0]);
          
          stances.push({
            stance: stanceName,
            description: longestDescription
          });
        }
      });
      
      // If we found any stance versions, return them
      if (stances.length > 0) {
        return stances;
      }
    }
    
    // If the above approaches didn't work, fall back to the original regex approach
    const bowMatch = description.match(/Bow:?\s+(.+?)(?=Axe:|$)/s);
    const axeMatch = description.match(/Axe:?\s+(.+?)(?=Bow:|$)/s);
    
    if (bowMatch && bowMatch[1]) {
      stances.push({
        stance: 'Bow',
        description: bowMatch[1].trim()
      });
    }
    
    if (axeMatch && axeMatch[1]) {
      stances.push({
        stance: 'Axe',
        description: axeMatch[1].trim()
      });
    }
    
    return stances;
  };
  
  const renderAbilityContent = (ability, index) => {
    // Check if this is a stance ability by data structure or description pattern
    const isStanceSwitcher = ability.is_stance_switcher || detectStanceDescription(ability);
    
    if (isStanceSwitcher) {
      // Get or parse stance versions
      const stanceVersions = ability.stance_versions && ability.stance_versions.length > 0 
        ? ability.stance_versions 
        : parseStanceVersions(ability);
      
      // If we still don't have stance versions, fall back to regular display
      if (!stanceVersions || stanceVersions.length === 0) {
        return renderRegularAbility(ability);
      }
      
      const activeStance = activeStances[index] || (stanceVersions[0] ? stanceVersions[0].stance : 'Bow');
      const activeStanceData = stanceVersions.find(sv => sv.stance === activeStance) || stanceVersions[0];
      
      return (
        <>
          <StanceTabsContainer>
            {stanceVersions.map((sv) => (
              <StanceTab 
                key={sv.stance}
                $active={activeStance === sv.stance}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStanceChange(index, sv.stance);
                }}
              >
                {sv.stance}
              </StanceTab>
            ))}
          </StanceTabsContainer>
          
          {/* Only show the base effect if it exists and it makes sense to show it */}
          {ability.base_effect && ability.name !== "Hail of Arrows" && (
          <AbilityDescription>
              {ability.base_effect}
          </AbilityDescription>
          )}
          
          {/* Show only the active stance description */}
          <AbilityDescription>
            <strong>{activeStance}:</strong> {activeStanceData.description}
          </AbilityDescription>
        </>
      );
    } else {
      return renderRegularAbility(ability);
    }
  };
  
  const renderRegularAbility = (ability) => {
      // Process the main description to remove duplicated bullet points
      let mainDescription = ability.description;
      let effectList = [];
      
      // If the ability has interactions, we'll clean up the main description
      // and create a separate list of interaction effects
      if (ability.has_interactions && ability.interaction_effects && ability.interaction_effects.length > 0) {
        // Extract the main effect (first sentence or base effect if available)
        mainDescription = ability.base_effect || ability.description.split('.')[0] + '.';
        
        // Create a list of effects without redundancy
        effectList = ability.interaction_effects.map(effect => effect.effect);
      }
    
    // Check for duplicate descriptions (same description appearing in description and interactions)
    if (effectList.length > 0 && mainDescription) {
      effectList = effectList.filter(effect => 
        !mainDescription.includes(effect) && 
        effect !== mainDescription
      );
      }
      
      return (
        <>
          <AbilityDescription>{mainDescription}</AbilityDescription>
          
          {/* Only display effects list if we have effects and they aren't empty */}
          {effectList.length > 0 && (
            <InteractionsList>
              {effectList.map((effect, idx) => (
                <InteractionItem key={idx}>{effect}</InteractionItem>
              ))}
            </InteractionsList>
          )}
          
          {ability.has_aspect && ability.aspect_description && (
            <AspectBox>
              <strong>Aspect:</strong> {ability.aspect_description}
            </AspectBox>
          )}
        </>
      );
  };

  // Prepare abilities for display - ensure we show all abilities and handle duplicates
  const prepareAbilities = () => {
    if (!god.abilities || god.abilities.length === 0) {
      return [];
    }
    
    // Process abilities to fix stance information
    const processedAbilities = god.abilities.map(ability => {
      // Clone the ability to avoid modifying the original
      const processedAbility = { ...ability };
      
      // Process all abilities with stance switching consistently
      if (!processedAbility.is_stance_switcher && detectStanceDescription(processedAbility)) {
        processedAbility.is_stance_switcher = true;
        
        // Parse stance versions
        const stanceVersions = parseStanceVersions(processedAbility);
        
        // Set the stance versions
        if (stanceVersions.length > 0) {
          processedAbility.stance_versions = stanceVersions;
          
          // For Hail of Arrows or similar abilities, we need to ensure we don't have duplicate content
          if (processedAbility.name === "Hail of Arrows" || 
              (processedAbility.description && 
               processedAbility.description.includes("Bow:") && 
               processedAbility.description.includes("Axe:"))) {
            
            // Don't set a base effect for these abilities - it causes duplicate content
            processedAbility.base_effect = null;
          }
          // For other abilities, try to extract a base effect if none exists
          else if (!processedAbility.base_effect) {
            // Try to extract common text before any stance is mentioned
            const firstStance = stanceVersions[0].stance;
            const stanceIndex = processedAbility.description.indexOf(firstStance + ':');
            if (stanceIndex > 0) {
              processedAbility.base_effect = processedAbility.description.substring(0, stanceIndex).trim();
            }
          }
        }
      }
      
      // Clean up any duplicate sections in the description
      if (processedAbility.description) {
        // Remove duplicate paragraphs
        if (processedAbility.description.includes('\n\n')) {
          const paragraphs = processedAbility.description.split('\n\n');
          if (paragraphs.length > 1) {
            // Find and remove duplicate paragraphs
            const uniqueParagraphs = [];
            for (const paragraph of paragraphs) {
              // Only add this paragraph if it's not already included in another one
              const isDuplicate = uniqueParagraphs.some(p => 
                p.includes(paragraph) || paragraph.includes(p)
              );
              
              if (!isDuplicate) {
                uniqueParagraphs.push(paragraph);
              } else {
                // If it's a duplicate but longer, replace the shorter one
                const shorterIdx = uniqueParagraphs.findIndex(p => 
                  p.includes(paragraph) || paragraph.includes(p)
                );
                if (shorterIdx !== -1 && paragraph.length > uniqueParagraphs[shorterIdx].length) {
                  uniqueParagraphs[shorterIdx] = paragraph;
                }
              }
            }
            
            processedAbility.description = uniqueParagraphs.join('\n\n');
          }
        }
      }
      
      return processedAbility;
    });
    
    // Create a map of ability types to ensure we don't duplicate stance abilities
    const abilityMap = new Map();
    
    // Process all abilities and group by type
    processedAbilities.forEach(ability => {
      const type = ability.ability_type;
      
      // If we've already seen this ability type, determine how to handle
      if (abilityMap.has(type)) {
        const existingAbility = abilityMap.get(type);
        
        // If they have the same name, they're variants of the same ability
        if (existingAbility.name === ability.name) {
          // Always prefer abilities with stance information
          if (!existingAbility.is_stance_switcher && ability.is_stance_switcher) {
            abilityMap.set(type, ability);
          }
          // If both have stance information, merge them if possible
          else if (existingAbility.is_stance_switcher && ability.is_stance_switcher) {
            // Keep the one with more stance versions, or with longer descriptions
            const existingStances = existingAbility.stance_versions || [];
            const newStances = ability.stance_versions || [];
            
            if (newStances.length > existingStances.length) {
              abilityMap.set(type, ability);
            }
            // If same number of stances, use the one with more detailed descriptions
            else if (newStances.length === existingStances.length && newStances.length > 0) {
              const existingChars = existingStances.reduce((sum, s) => sum + (s.description?.length || 0), 0);
              const newChars = newStances.reduce((sum, s) => sum + (s.description?.length || 0), 0);
              
              if (newChars > existingChars) {
                abilityMap.set(type, ability);
              }
            }
          }
          // Otherwise keep the one with the longer description
          else if (ability.description && (!existingAbility.description || 
                  ability.description.length > existingAbility.description.length)) {
            abilityMap.set(type, ability);
          }
        }
        // Different abilities with the same type (should be rare)
        else {
          abilityMap.set(`${type}_${ability.name}`, ability);
        }
      } else {
        // First time seeing this ability type
        abilityMap.set(type, ability);
      }
    });
    
    // Convert map back to array
    return Array.from(abilityMap.values());
  };
  
  const displayAbilities = prepareAbilities();

  return (
    <>
    <CardContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
        <GodImage $image={imageUrl} />
      <GodInfo>
        <GodName>{god.name}</GodName>
        <GodTitle>{god.title}</GodTitle>
        <PantheonBadge>{god.pantheon}</PantheonBadge>
        
        <TabButtons>
          <TabButton 
              $active={activeTab === 'stats'} 
            onClick={() => setActiveTab('stats')}
          >
            Base Stats
          </TabButton>
          <TabButton 
              $active={activeTab === 'abilities'} 
            onClick={() => setActiveTab('abilities')}
          >
            Abilities
          </TabButton>
        </TabButtons>
        
        {activeTab === 'stats' && (
          <StatsContainer>
            <StatsTitle>Base Stats</StatsTitle>
            <StatsGrid>
              {mainStats.map((stat, index) => (
                <StatItem key={index}>
                  <StatName>{stat.name}</StatName>
                  <StatValue>{stat.value}</StatValue>
                </StatItem>
              ))}
            </StatsGrid>
          </StatsContainer>
        )}
        
        {activeTab === 'abilities' && (
          <AbilitiesTabs>
            <AbilitiesTitle>Abilities</AbilitiesTitle>
            <div>
              {displayAbilities.map((ability, index) => (
                <AbilityContainer key={index}>
                    <AbilityHeader onClick={() => handleAbilityClick(ability)}>
                    <AbilityNameSection>
                        <AbilityIcon $image={ability.image_url} />
                      <AbilityName>{ability.name}</AbilityName>
                    </AbilityNameSection>
                    <AbilityType>{ability.ability_type}</AbilityType>
                  </AbilityHeader>
                  {renderAbilityContent(ability, index)}
                </AbilityContainer>
              ))}
                {displayAbilities.length === 0 && (
                <p>No ability information available</p>
              )}
            </div>
          </AbilitiesTabs>
        )}
      </GodInfo>
    </CardContainer>
      
      <InfoPopup
        data={selectedAbility}
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        type="ability"
      />
    </>
  );
};

export default GodCard; 